import type { RefObject } from 'react';
import type { RichTextEditorRef } from '@cotera/client/app/components/app/rte';
import type { Editor } from '@tiptap/react';
import {
  BaseModel,
  createProvidedModelContext,
  MixedSourceWatchable,
  ReadonlyEventWatchable,
  ReadonlyWatchable,
  TargetableModel,
  Watchable,
  type DeveloperDetails,
} from '@cotera/client/v0/actions/framework';
import { BroadcastEventType } from '@cotera/api';
import type { BroadcastEventPayloadMap } from '@cotera/api';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { Agent } from '@cotera/sdk/core';
import { DG } from '@cotera/sdk/core';
import type { AgentApiData, AgentResourceApiData } from './use-agent-query';
import { toAgentApiData } from './use-agent-query';
import { agentFromApiData } from './agent-from-api-data';
import { WatchablesFactory } from '../watchables/watchables-factory';
import type { UrlParamAdapter } from '@cotera/client/app/etc/url-param-watchable';
import { UrlParamWatchable } from '@cotera/client/app/etc/url-param-watchable';
import {
  EDITOR_TAB_QUERY_KEY,
  parseAgentEditorTabFromUrl,
  serializeAgentEditorTabToUrl,
} from '../etc/editor-tab-url';

function transformAgent(data: AgentApiData): Agent {
  return agentFromApiData(data);
}

type AgentModelProps = {
  agentId: string;
  orgId: string;
  userId: string;
  isNew: boolean;
  mode: 'artifact' | 'page';
  client: Pick<TenantedClient, 'resource'>;
  watchablesFactory: Pick<WatchablesFactory, 'makeAgentResourceStateWatchable'>;
  urlAdapter: UrlParamAdapter;
};

type AgentResourceState = {
  resource: AgentResourceApiData | null;
  status: 'loading' | 'ready' | 'not-found';
};

function createInitialAgentResource(
  orgId: string,
  userId: string,
  agentId: string
): AgentResourceApiData {
  return {
    agent: toAgentApiData({
      ...Agent.createNew(orgId, userId, agentId),
      id: agentId,
      title: null,
      locked: false,
    }),
    meta: null,
  };
}

export type AgentEditorTab = 'editor' | 'chats';

export class AgentModel extends BaseModel implements TargetableModel {
  readonly resourceType = 'agent' as const;
  private _isTargeted = true;
  readonly titleWatchable: ReadonlyWatchable<string | null>;
  readonly editorStateWatchable: MixedSourceWatchable<DG.DocumentEditor.DocumentState>;
  readonly modelWatchable: MixedSourceWatchable<string>;
  readonly editorWatchable: ReadonlyWatchable<DG.DocumentEditor.EditorState>;
  readonly hasUnsavedChangesWatchable: Watchable<boolean>;
  /** See `DocumentModel.editorInstanceWatchable`. */
  readonly editorInstanceWatchable: Watchable<Editor | null>;
  /** Resource wrapper (agent + meta like folderId/permission). */
  readonly resourceWatchable: ReadonlyWatchable<AgentResourceApiData | null>;
  readonly loadStatusWatchable: ReadonlyWatchable<
    'loading' | 'ready' | 'not-found'
  >;

  /** Ref to the RichTextEditor instance. Used to sync external editorState changes into the editor. */
  readonly editorRef: RefObject<RichTextEditorRef | null>;

  readonly isNew: boolean;

  readonly agentId: string;

  readonly agentKey: string;

  readonly mode: 'artifact' | 'page';

  readonly lockedWatchable: ReadonlyWatchable<boolean>;

  readonly editorTabWatchable: ReadonlyWatchable<AgentEditorTab>;

  private readonly _editorTabWatchable: Watchable<AgentEditorTab>;
  private readonly _editorTabUrl: UrlParamWatchable<AgentEditorTab>;

  private readonly agentDataEventWatchable: ReadonlyEventWatchable<AgentResourceState>;

  private readonly _dispose: () => void;

  constructor(props: AgentModelProps) {
    super(props.agentId);

    const { agentId, client } = props;
    const initialResource = createInitialAgentResource(
      props.orgId,
      props.userId,
      agentId
    );
    const initialState: AgentResourceState =
      props.isNew === true
        ? { resource: initialResource, status: 'ready' }
        : { resource: null, status: 'loading' };

    this.agentId = agentId;
    const agent = transformAgent(initialResource.agent);
    this.agentKey = agent.agentKey;
    this.isNew = props.isNew;
    this.mode = props.mode;

    const watchablesFactory = props.watchablesFactory;

    this.agentDataEventWatchable =
      watchablesFactory.makeAgentResourceStateWatchable({
        agentId,
        client,
        initialValue: initialState,
      });

    this.resourceWatchable = Watchable.from(
      (get) => get(this.agentDataEventWatchable).resource
    );
    this.loadStatusWatchable = Watchable.from(
      (get) => get(this.agentDataEventWatchable).status
    );

    this.lockedWatchable = Watchable.from(
      (get) => get(this.agentDataEventWatchable).resource?.agent.locked === true
    );

    this.editorStateWatchable = MixedSourceWatchable.from(
      (get) => {
        const resource = get(this.agentDataEventWatchable).resource;
        const agentData = resource?.agent ?? initialResource.agent;
        return transformAgent(agentData).editorState.getState();
      },
      {
        shouldAcceptDerived: (derived, override) => {
          if (override === null) {
            return true;
          }
          return JSON.stringify(derived) === JSON.stringify(override);
        },
      }
    );

    this.editorRef = { current: null };
    const unsubEditorStateSync = this.editorStateWatchable.subscribe(
      (newState) => {
        const editor = this.editorRef.current?.editor;
        if (editor === null || editor === undefined) {
          return;
        }
        const currentJson = editor.getJSON();
        if (JSON.stringify(currentJson) === JSON.stringify(newState)) {
          return;
        }
        editor.commands.setContent(newState, { emitUpdate: false });
      }
    );

    this.editorWatchable = Watchable.from((get) => {
      return new DG.DocumentEditor.EditorState(get(this.editorStateWatchable));
    });

    this.titleWatchable = Watchable.from((get) => {
      const a = get(this.editorWatchable);
      return a.title() ?? null;
    });

    this.modelWatchable = MixedSourceWatchable.from((get) => {
      const resource = get(this.agentDataEventWatchable).resource;
      const agentData = resource?.agent ?? initialResource.agent;
      return transformAgent(agentData).model;
    });

    this.hasUnsavedChangesWatchable = Watchable.fromValue(false);
    this.editorInstanceWatchable = Watchable.fromValue<Editor | null>(null);

    const editorTabUrl = new UrlParamWatchable<AgentEditorTab>(
      EDITOR_TAB_QUERY_KEY,
      {
        default: 'editor',
        parse: parseAgentEditorTabFromUrl,
        serialize: serializeAgentEditorTabToUrl,
      },
      props.urlAdapter,
      'editor'
    );
    this._editorTabUrl = editorTabUrl;
    this._editorTabWatchable = editorTabUrl.asWatchable();
    this.editorTabWatchable = this._editorTabWatchable;

    this._dispose = () => {
      unsubEditorStateSync();
      this.modelWatchable.unsubscribe();
      this.agentDataEventWatchable.unsubscribe();
    };
  }

  /** Mark that the user has made changes on the FE. Used for navigation blocking. */
  markUserEdited(): void {
    this.hasUnsavedChangesWatchable.set(true);
  }

  /**
   * Updates the unsaved flag from the current editor doc vs the last-known server
   * resource (query / broadcast). Avoids staying "dirty" after autosave or when the
   * doc matches the server baseline again (e.g. undo), which would otherwise keep
   * blocking navigation until the next full render path.
   */
  reconcileHasUnsavedAfterEditorUpdate(
    editorJson: DG.DocumentEditor.DocumentState
  ): void {
    const resource = this.resourceWatchable.snapshot();
    if (resource === null) {
      return;
    }
    const server = transformAgent(resource.agent);
    const serverDoc = server.editorState.getState();
    const serverModel = server.model;
    const localModel = this.modelWatchable.snapshot();
    if (
      JSON.stringify(editorJson) === JSON.stringify(serverDoc) &&
      localModel === serverModel
    ) {
      this.hasUnsavedChangesWatchable.set(false);
    } else {
      this.hasUnsavedChangesWatchable.set(true);
    }
  }

  /** Re-run unsaved detection from the current editor + model watchables (e.g. after model picker changes). */
  reconcileHasUnsavedFromResourceSnapshot(): void {
    this.reconcileHasUnsavedAfterEditorUpdate(
      this.editorStateWatchable.snapshot()
    );
  }

  /** Re-fetch the agent resource from the server (e.g. after a side-channel update). */
  refreshResource(): Promise<void> {
    return this.agentDataEventWatchable.refresh();
  }

  setEditorTab(tab: AgentEditorTab): void {
    if (this._editorTabWatchable.snapshot() === tab) {
      return;
    }
    this._editorTabUrl.set(tab);
  }

  /** Sync agent/chats tab from the chat URL (e.g. browser back/forward). */
  syncEditorTabFromUrl(searchParams: URLSearchParams): void {
    const raw = searchParams.get(EDITOR_TAB_QUERY_KEY);
    if (raw === null || raw === '') {
      this._editorTabWatchable.setFromSource('editor');
      return;
    }
    this._editorTabUrl.syncFromUrl(searchParams);
  }

  /** Optimistically patch the agent's emoji field in place (bypasses refetch). */
  setAgentEmoji(emoji: string | null): Promise<void> {
    const current = this.agentDataEventWatchable.snapshot().resource;
    if (current === null) {
      return Promise.resolve();
    }
    const payload: BroadcastEventPayloadMap[typeof BroadcastEventType.ResourceUpdated] =
      {
        orgId: current.agent.orgId,
        resourceType: 'agent',
        resourceId: current.agent.id,
        agent: {
          ownerId: current.agent.ownerId,
          id: current.agent.id,
          orgId: current.agent.orgId,
          agentKey: current.agent.agentKey ?? '',
          editorState: current.agent.editorState,
          model: current.agent.model,
          locked: current.agent.locked,
          createdAt: current.agent.createdAt,
          updatedAt: current.agent.updatedAt,
          title: current.agent.title,
          description: null,
          emoji,
        },
      };
    return this.agentDataEventWatchable.refresh(payload);
  }

  /** Call after a successful save to update the baseline for unsaved-change detection */
  markAsSaved(_editorState: DG.DocumentEditor.DocumentState): void {
    this.hasUnsavedChangesWatchable.set(false);
  }

  /** Get editorState for save: editable if set, else from initial data */
  getEditorStateForSave(): DG.DocumentEditor.DocumentState {
    return this.editorStateWatchable.snapshot();
  }

  /**
   * True when the editor content differs from the last-known server baseline.
   *
   * A model-only save should NOT re-send `editorState`: on an untouched editor
   * the snapshot is the raw persisted document verbatim (see
   * `agentFromApiData` → `parseEditorState`, a passthrough), which may be a
   * legacy/unnormalized shape that the strict update schema rejects with
   * `ApiSchemaValidationError`. Omitting it lets the server keep the existing
   * state and changes just the model. Once the user edits the body, TipTap
   * re-serializes it into a schema-valid shape, so we send it normally.
   *
   * Returns `true` (send it) when there is no server baseline yet.
   */
  editorStateChangedFromServer(): boolean {
    const resource = this.resourceWatchable.snapshot();
    if (resource === null) {
      return true;
    }
    const serverDoc = transformAgent(resource.agent).editorState.getState();
    return (
      JSON.stringify(this.editorStateWatchable.snapshot()) !==
      JSON.stringify(serverDoc)
    );
  }

  /** Get model for save: editable if set, else from initial data */
  getModelForSave(): string {
    return this.modelWatchable.snapshot();
  }

  dispose(): void {
    this._dispose();
  }

  get isTargeted(): boolean {
    return this._isTargeted;
  }

  target(): void {
    this._isTargeted = true;
  }

  markAsNotTargeted(): void {
    this._isTargeted = false;
  }

  isEqualTo(other: AgentModel): boolean {
    return this.id === other.id;
  }

  developerDetails(): DeveloperDetails {
    const resource = this.resourceWatchable.snapshot();
    const agent = resource?.agent;
    const entries = [
      { key: 'Agent ID', value: this.agentId },
      { key: 'Agent key', value: this.agentKey },
      { key: 'Model', value: this.modelWatchable.snapshot() },
    ];

    if (agent?.orgId !== undefined) {
      entries.push({ key: 'Org ID', value: agent.orgId });
    }
    if (agent?.ownerId !== undefined) {
      entries.push({ key: 'Owner ID', value: agent.ownerId });
    }

    return {
      title: 'Agent',
      entries,
    };
  }
}

const agentModelContext = createProvidedModelContext(
  AgentModel,
  'AgentModel not found in scope'
);

/**
 * Provides the agent model instance for the current editor tree so
 * `useAgentModel` binds correctly when several agents share one actions scope.
 * Pass `target` for the active artifact tab only.
 */
export const AgentModelProvider = agentModelContext.Provider;

export const useAgentModel = agentModelContext.useModel;
